import { ActionSheetButtonStyle, ActionSheetPlugin } from "@capacitor/action-sheet";
import { AppLauncherPlugin } from "@capacitor/app-launcher";
import { History } from "history";
import { DateTime } from "luxon";
import { ExtractRouteParams, generatePath, matchPath } from "react-router";
import { LocalStorageFilterValueTypes } from "src/components/molecules/FilterBar/types";
import { shallowEqualArrays } from "swing-utils";

import { AUTH_URLS, createMoreUrl, createRequestsUrl } from "~pages";
import { msg, type OpeningsData, type RequestData } from "~utils";

export function centsToDollars(valueInCents: number): string {
  return (valueInCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function payDisplay(amount: string, suffix = "", seperator = "/") {
  if (suffix == "") {
    return `${amount}${suffix}`;
  } else {
    return `${amount}${seperator}${suffix}`;
  }
}

export function isMultiDay(intervals: RequestData["intervals"]) {
  return intervals.length > 1;
}

export function requestTimeDisplay(data: OpeningsData) {
  return isMultiDay(data.intervals)
    ? `${data.intervals.length} ${msg("REQUEST_TIME_UNIT")}`
    : data.displayTime;
}

const GRACE_PERIOD_CUTOFF_IN_MINS = 30;
const PENALTY_PERIOD_CUTOFF_IN_HRS = 24;

export type CancelPenaltyType =
  | "NO_PENALTY"
  | "PENALTY_REQUEST_STARTED"
  | "PENALTY_REQUEST_NOT_STARTED";

export type CalcCancelPenaltyTypeProps = {
  reqStartTime: string | DateTime;
  reqFillTime: string | DateTime;
  reqCancelTime: string | DateTime;
};

// NOTE: Expects `props` keys to be one of two types:
// 1. a date string in ISO format set to UTC 0
// 2. a DateTime set to UTC
export function calcCancelPenaltyType(props: CalcCancelPenaltyTypeProps): CancelPenaltyType {
  let { reqStartTime, reqFillTime, reqCancelTime } = props;

  if (typeof reqStartTime === "string") {
    reqStartTime = DateTime.fromISO(reqStartTime, { zone: "utc" });
  }

  if (typeof reqFillTime === "string") {
    reqFillTime = DateTime.fromISO(reqFillTime, { zone: "utc" });
  }

  if (typeof reqCancelTime === "string") {
    reqCancelTime = DateTime.fromISO(reqCancelTime, { zone: "utc" });
  }

  const graceThreshold = reqFillTime.plus({
    minutes: GRACE_PERIOD_CUTOFF_IN_MINS,
  });
  const startThreshold = reqStartTime.minus({
    hours: PENALTY_PERIOD_CUTOFF_IN_HRS,
  });
  const isCancelTimeInsideGracePeriod = reqCancelTime <= graceThreshold;
  const isCancelTimeOutsidePenaltyPeriod = reqCancelTime <= startThreshold;
  const isCancelTimeAfterRequestStart = reqCancelTime > reqStartTime;

  if (isCancelTimeAfterRequestStart) {
    return "PENALTY_REQUEST_STARTED";
  }

  if (isCancelTimeInsideGracePeriod || isCancelTimeOutsidePenaltyPeriod) {
    return "NO_PENALTY";
  }

  return "PENALTY_REQUEST_NOT_STARTED";
}

/**
 * Calculates the number of remaining intervals that have not yet ended.
 * Includes intervals that are currently active (i.e., their endDate is after the current time).
 *
 * Intervals are counted as "remaining" if their endDate is **strictly after**
 * the provided or current UTC time, measured to the second.
 * If the current time is before the start of the first interval, returns 0.
 *
 * @param intervals - Array of interval objects with ISO endDate strings
 * @param now - Optional DateTime to use as the current time (UTC). Defaults to the actual current time.
 * @returns Number of intervals remaining as of the provided or current time.
 */
export function calcRemainingDays(
  intervals: RequestData["intervals"],
  now: DateTime = DateTime.now().setZone("utc").startOf("second"),
): number {
  if (!intervals.length) return 0;

  const firstStart = DateTime.fromISO(intervals[0].startDate, { zone: "utc" }).startOf("second");

  // If the request hasn't started yet, return 0
  if (now < firstStart) return 0;

  return intervals.filter((interval) => {
    const end = DateTime.fromISO(interval.endDate, { zone: "utc" }).startOf("second");
    return end > now;
  }).length;
}

type GenerateGoogleMapsLinkProps = Partial<{
  street: string;
  city: string;
  state: string;
  zip: string;
}>;

export function generateGoogleMapsLink(props: GenerateGoogleMapsLinkProps): string {
  const { street, city, state, zip } = props;

  return `http://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${street} ${city}, ${state}, ${zip}`,
  )}`;
}

// based on whether request is hourly
export function formatPay(isHourly: boolean, paymentInCents: number) {
  const dollars = centsToDollars(paymentInCents);
  if (isHourly) {
    return `${payDisplay(dollars, "hr")}`;
  }
  return `${dollars}`;
}

export function maybePluralize(str: string, count: number): string {
  if (count === 1) {
    return `${count} ${str}`;
  } else {
    return `${count} ${str}s`;
  }
}

// Header Navigation
/**
 * Generates the "back" URL based on current path.
 *
 * This logic is here to handle nested page situations where the logical
 * `history.goBack()` is not the expected behaviour.
 *
 * @example
 * ```text
 * - Given that we are HAVE NO history
 *   And we are on the `/more/timesheet/:weekId?` page
 * - When clicking the "back" button (which then calls this function)
 * - Then expect to go to the `/more` page
 * ```
 */
export function generateBackNavigation(history: History) {
  const splitPathName = history.location.pathname.toString().split("/").filter(Boolean);
  splitPathName.pop();

  // to account for no school list page in sub app
  if (splitPathName.includes("school") || splitPathName.includes("skill-builder")) {
    splitPathName.pop();
  }

  // Handle the `/more/timesheet/:weekId?` page
  const timesheetPageMatch = matchPath<ExtractRouteParams<typeof AUTH_URLS.timesheet, string>>(
    history.location.pathname,
    { path: AUTH_URLS.timesheet },
  );
  if (timesheetPageMatch?.isExact) {
    return history.push(createMoreUrl());
  }

  return splitPathName.length > 0
    ? history.push(generatePath(`/${splitPathName.join("/")}`))
    : history.push(createRequestsUrl());
}

export const formatDisplayTimes = (times: string) => {
  return times.replace("AM", " am").replace("PM", " pm");
};

export function formatStringArray(items: string[]) {
  return items.join(", ");
}

export type HandleMapLinkNavigationParams = {
  getPlatform: () => string;
  appLauncher: AppLauncherPlugin;
  actionSheet: ActionSheetPlugin;
  openLinkInApp: (fullUrl: string) => Promise<void>;
  link: string;
  query: string;
};

export async function handleMapLinkNavigation(
  event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  params: HandleMapLinkNavigationParams,
) {
  const { getPlatform, appLauncher, actionSheet, openLinkInApp, link, query } = params;

  // default href behavior when on web
  if (getPlatform() === "web") {
    return;
  }

  // prevent default when on native app
  event.preventDefault();

  // if Android then check for Google Maps. Fallback to Google Maps in browser
  if (getPlatform() === "android") {
    try {
      const isGoogleMapsAvailable = await appLauncher.canOpenUrl({ url: "comgooglemaps://" });
      if (isGoogleMapsAvailable.value) {
        const isCompleted = await appLauncher.openUrl({
          url: `comgooglemaps://?q=${query}`,
        });
        // if attempt to open Google Maps app fails, 'completed' will be false so open in browser
        if (!isCompleted.completed) {
          return await openLinkInApp(link);
        }
      }
      // if Google Maps app is not available, open in browser
      return await openLinkInApp(link);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Unable to open Google Maps app or website on Android", err);
    }
  }

  // if iOS then check for both Apple Maps and Google Maps. When both available, present prompt otherwise open in one app or the other. Fallback to Google Maps in browser
  if (getPlatform() === "ios") {
    try {
      const isAppleMapsAvailable = await appLauncher.canOpenUrl({ url: "http://maps.apple.com" });
      const isGoogleMapsAvailable = await appLauncher.canOpenUrl({ url: "comgooglemaps://" });

      // when both apps are available, show prompt allowing user to decide which app to open
      if (isAppleMapsAvailable.value && isGoogleMapsAvailable.value) {
        const actionSheetResult = await actionSheet.showActions({
          title: "Open In...",
          options: [
            { title: "Apple Maps" },
            { title: "Google Maps" },
            { title: "Cancel", style: ActionSheetButtonStyle.Cancel },
          ],
        });

        // Apple Maps
        if (actionSheetResult.index === 0) {
          return await appLauncher.openUrl({
            url: `http://maps.apple.com?q=${query}`,
          });
        }
        // Google Maps
        if (actionSheetResult.index === 1) {
          return await appLauncher.openUrl({
            url: `comgooglemaps://?q=${query}`,
          });
        }
        // Cancel
        if (actionSheetResult.index === 2) {
          // just exit function
          return;
        }
      }

      // when only Apple Maps available
      if (isAppleMapsAvailable.value) {
        const isCompleted = await appLauncher.openUrl({
          url: `http://maps.apple.com?q=${query}`,
        });
        // if Apple Maps app fails to open then `completed` will be false so open in browser
        if (!isCompleted.completed) {
          return await openLinkInApp(link);
        }
        return;
      }

      // when only Google Maps available
      if (isGoogleMapsAvailable.value) {
        const isCompleted = await appLauncher.openUrl({
          url: `comgooglemaps://?q=${query}`,
        });
        // if Google Maps app fails to open then `completed` will be false so open in browser
        if (!isCompleted.completed) {
          return await openLinkInApp(link);
        }
        return;
      }

      // when neither Apple Maps or Google Maps apps are available
      if (!isAppleMapsAvailable.value && !isGoogleMapsAvailable.value) {
        // open Google Maps in browser
        return await openLinkInApp(link);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Unable to open Google Maps app or website on iOS", err);
    }
  }
}

export function formatCityStateZip(address?: OpeningsData["school"]["address"]) {
  if (!address) return "";
  const { city, state, zip } = address;
  /**
   * city | state | zip
   *  -      -      -
   *  x      -      -
   *  -      x      -
   *  -      -      x
   *  x      x      -
   *  x      -      x
   *  -      x      x
   *  x      x      x
   */
  if (!city && !state && !zip) return "";
  if (city && !state && !zip) return city;
  if (!city && state && !zip) return state;
  if (!city && !state && zip) return zip;
  if (city && state && !zip) return `${city}, ${state}`;
  if (city && !state && zip) return `${city}, ${zip}`;
  if (!city && state && zip) return `${state} ${zip}`;
  if (city && state && zip) return `${city}, ${state} ${zip}`;
}

// Accepts an ISO time string in UTC and outputs difference
// from current time in minutes
export function calcDifferenceFromNow(timestamp: string) {
  return DateTime.utc().diff(DateTime.fromISO(timestamp, { zone: "utc" }), "minutes").minutes;
}

export function areFiltersSet(LSValues: LocalStorageFilterValueTypes) {
  return (
    LSValues.requestLength.length !== 0 ||
    LSValues.requestType.length !== 0 ||
    LSValues.startingDate.length !== 0
  );
}

export const isTempStorageChanged = (
  TempValues: LocalStorageFilterValueTypes,
  LSValues: LocalStorageFilterValueTypes,
) => {
  // Form has been touched if any of these are true:
  return (
    !shallowEqualArrays(LSValues.startingDate, TempValues.startingDate) ||
    !shallowEqualArrays(LSValues.requestLength, TempValues.requestLength) ||
    !shallowEqualArrays(LSValues.requestType, TempValues.requestType)
  );
};

export const isLongTerm = (request: RequestData) => {
  const start = new Date(request.startDate);
  const end = new Date(request.endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return false;
  }

  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const diffMs = Math.abs(end.getTime() - start.getTime());

  return diffMs > 21 * MS_PER_DAY;
};
